// src/pages/inventory/InventoryTransactions.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    FormHelperText
} from '@mui/material';
import {
    Add as AddIcon,
    ArrowUpward as InboundIcon,
    ArrowDownward as OutboundIcon,
    SwapHoriz as TransferIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';

const InventoryTransactions = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [transactionType, setTransactionType] = useState('entrada');
    const [currentTransaction, setCurrentTransaction] = useState({
        item_id: '',
        warehouse_id: '',
        quantity: 1,
        transaction_type: 'entrada',
        reference_type: '',
        notes: ''
    });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            // Fetch transactions
            const transactionsResponse = await fetch('/api/inventory/transactions', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!transactionsResponse.ok) {
                throw new Error('Error al obtener transacciones');
            }

            const transactionsData = await transactionsResponse.json();
            setTransactions(transactionsData);

            // Fetch products
            const productsResponse = await fetch('/api/inventory/items', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!productsResponse.ok) {
                throw new Error('Error al obtener productos');
            }

            const productsData = await productsResponse.json();
            setProducts(productsData);

            // Fetch warehouses
            const warehousesResponse = await fetch('/api/inventory/warehouses', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!warehousesResponse.ok) {
                throw new Error('Error al obtener almacenes');
            }

            const warehousesData = await warehousesResponse.json();
            setWarehouses(warehousesData);

        } catch (error) {
            console.error('Error fetching inventory data:', error);
            setError(error.message || 'Error al cargar datos de inventario');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (type = 'entrada') => {
        setTransactionType(type);
        setCurrentTransaction({
            item_id: '',
            warehouse_id: '',
            quantity: 1,
            transaction_type: type,
            reference_type: '',
            notes: ''
        });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentTransaction(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            if (!currentTransaction.item_id || !currentTransaction.warehouse_id || currentTransaction.quantity < 1) {
                setError('Por favor complete todos los campos requeridos');
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await fetch('/api/inventory/transactions', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(currentTransaction)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al registrar transacción');
            }

            setSuccess('Transacción registrada exitosamente');
            setTimeout(() => setSuccess(null), 5000);

            handleCloseDialog();
            fetchData();
        } catch (error) {
            console.error('Error submitting transaction:', error);
            setError(error.message || 'Error al registrar transacción');
        } finally {
            setLoading(false);
        }
    };

    const getReferenceTypeOptions = () => {
        if (transactionType === 'entrada') {
            return [
                { value: 'purchase', label: 'Compra' },
                { value: 'return', label: 'Devolución' },
                { value: 'adjustment', label: 'Ajuste de Inventario' }
            ];
        } else if (transactionType === 'salida') {
            return [
                { value: 'installation', label: 'Instalación' },
                { value: 'sale', label: 'Venta' },
                { value: 'defective', label: 'Producto Defectuoso' },
                { value: 'adjustment', label: 'Ajuste de Inventario' }
            ];
        } else if (transactionType === 'transferencia') {
            return [
                { value: 'warehouse_transfer', label: 'Transferencia entre Almacenes' }
            ];
        }

        return [];
    };

    const getTransactionTypeChip = (type) => {
        switch (type) {
            case 'entrada':
                return <Chip label="Entrada" color="success" size="small" icon={<InboundIcon />} />;
            case 'salida':
                return <Chip label="Salida" color="error" size="small" icon={<OutboundIcon />} />;
            case 'transferencia':
                return <Chip label="Transferencia" color="primary" size="small" icon={<TransferIcon />} />;
            default:
                return <Chip label={type} size="small" />;
        }
    };

    const columns = [
        {
            field: 'created_at',
            headerName: 'Fecha',
            width: 150,
            valueFormatter: (param) => {
                const val = (param && typeof param === 'object' && 'value' in param) ? param.value : param;
                if (!val) return '-';
                try {
                    const d = new Date(val);
                    return isNaN(d.getTime()) ? '-' : format(d, 'dd/MM/yyyy HH:mm');
                } catch (e) {
                    return '-';
                }
            }
        },
        {
            field: 'transaction_type',
            headerName: 'Tipo',
            width: 150,
            renderCell: (params) => getTransactionTypeChip(params.value || 'desconocido')
        },
        {
            field: 'item_name',
            headerName: 'Producto',
            width: 200,
            valueFormatter: (param) => {
                const val = (param && typeof param === 'object' && 'value' in param) ? param.value : param;
                return val || 'Desconocido';
            }
        },
        {
            field: 'warehouse_name',
            headerName: 'Almacén',
            width: 150,
            valueFormatter: (param) => {
                const val = (param && typeof param === 'object' && 'value' in param) ? param.value : param;
                return val || 'Desconocido';
            }
        },
        {
            field: 'quantity',
            headerName: 'Cantidad',
            width: 100,
            type: 'number'
        },
        {
            field: 'reference_type',
            headerName: 'Referencia',
            width: 150,
            valueFormatter: (param) => {
                const val = (param && typeof param === 'object' && 'value' in param) ? param.value : param;
                const referenceTypes = {
                    'purchase': 'Compra',
                    'return': 'Devolución',
                    'adjustment': 'Ajuste',
                    'installation': 'Instalación',
                    'sale': 'Venta',
                    'defective': 'Defectuoso',
                    'warehouse_transfer': 'Transferencia'
                };

                return val ? (referenceTypes[val] || val) : '-';
            }
        },
        {
            field: 'user_name',
            headerName: 'Usuario',
            width: 150,
            valueFormatter: (param) => {
                const val = (param && typeof param === 'object' && 'value' in param) ? param.value : param;
                return val || 'Sistema';
            }
        },
        {
            field: 'notes',
            headerName: 'Notas',
            width: 200,
            valueFormatter: (param) => {
                const val = (param && typeof param === 'object' && 'value' in param) ? param.value : param;
                return val || '-';
            }
        }
    ];

    const filteredTransactions = transactions.filter(transaction => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();

        return (
            (transaction.item_name && transaction.item_name.toLowerCase().includes(searchLower)) ||
            (transaction.warehouse_name && transaction.warehouse_name.toLowerCase().includes(searchLower)) ||
            (transaction.user_name && transaction.user_name.toLowerCase().includes(searchLower)) ||
            (transaction.notes && transaction.notes.toLowerCase().includes(searchLower))
        );
    });

    const selectedProduct = products.find(p => p.id === currentTransaction.item_id);
    const isSerializedInbound = selectedProduct && !!selectedProduct.equipment_type && transactionType === 'entrada';

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Movimientos de Inventario
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <TextField
                    placeholder="Buscar movimientos..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<InboundIcon />}
                        onClick={() => handleOpenDialog('entrada')}
                    >
                        Entrada
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<OutboundIcon />}
                        onClick={() => handleOpenDialog('salida')}
                    >
                        Salida
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<TransferIcon />}
                        onClick={() => handleOpenDialog('transferencia')}
                    >
                        Transferencia
                    </Button>
                </Box>
            </Box>

            <DataGrid
                rows={filteredTransactions}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                autoHeight
                loading={loading}
                getRowId={(row) => row.id}
                sx={{ bgcolor: 'background.paper' }}
            />

            {/* Dialog for Add Transaction */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {transactionType === 'entrada' && 'Registrar Entrada de Inventario'}
                    {transactionType === 'salida' && 'Registrar Salida de Inventario'}
                    {transactionType === 'transferencia' && 'Registrar Transferencia de Inventario'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        {isSerializedInbound && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <strong>Ingreso Restringido:</strong> Este producto es un equipo serializado. Debe registrar su entrada y configurar sus series (S/N y MAC) a través de la sección de <strong>Compras/Series</strong>.
                            </Alert>
                        )}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Producto</InputLabel>
                                    <Select
                                        name="item_id"
                                        value={currentTransaction.item_id}
                                        onChange={handleInputChange}
                                        label="Producto"
                                    >
                                        {products.map((product) => (
                                            <MenuItem key={product.id} value={product.id}>
                                                {product.name} ({product.total_stock} en stock)
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Almacén</InputLabel>
                                    <Select
                                        name="warehouse_id"
                                        value={currentTransaction.warehouse_id}
                                        onChange={handleInputChange}
                                        label="Almacén"
                                    >
                                        {warehouses.map((warehouse) => (
                                            <MenuItem key={warehouse.id} value={warehouse.id}>
                                                {warehouse.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Tipo de Referencia</InputLabel>
                                    <Select
                                        name="reference_type"
                                        value={currentTransaction.reference_type}
                                        onChange={handleInputChange}
                                        label="Tipo de Referencia"
                                    >
                                        {getReferenceTypeOptions().map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        Motivo o propósito de la transacción
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="number"
                                    label="Cantidad"
                                    name="quantity"
                                    value={currentTransaction.quantity}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        inputProps: { min: 1 }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Notas"
                                    name="notes"
                                    value={currentTransaction.notes}
                                    onChange={handleInputChange}
                                    placeholder="Detalles adicionales sobre esta transacción..."
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!currentTransaction.item_id || !currentTransaction.warehouse_id || currentTransaction.quantity < 1 || isSerializedInbound}
                    >
                        Registrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventoryTransactions;